import React from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useInstructorCourses } from '@/hooks'
import type { InstructorCourseResponse } from '@/types/instructorSplit'

export const InstructorCoursesPage: React.FC = () => {
    const { user } = useAuth()
    const { data: myCourses, isLoading } = useInstructorCourses(user?.id || '')

    return (
        <div>
            <PageHeader
                title="My Courses"
                subtitle="Courses where you are assigned as instructor"
            />

            {/* Courses Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoading ? (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-text-muted">Loading courses...</div>
                ) : !myCourses || myCourses.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-text-muted">
                        <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[32px] text-gray-500">school</span>
                        </div>
                        <p className="text-gray-900 dark:text-white font-bold mb-2">No courses assigned</p>
                        <p className="text-sm">You haven't been assigned to any courses yet.</p>
                    </div>
                ) : (
                    myCourses.map((course: InstructorCourseResponse) => (
                        <div key={course.courseId} className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden group hover:border-primary/20 transition-colors">
                            {/* Thumbnail */}
                            <div
                                className="h-40 bg-cover bg-center bg-gray-700 relative"
                                style={{ backgroundImage: course.courseThumbnailUrl ? `url(${course.courseThumbnailUrl})` : undefined }}
                            >
                                <div className="absolute top-3 left-3">
                                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${course.courseType === 'hybrid'
                                            ? 'bg-blue-500/90 text-white'
                                            : 'bg-green-500/90 text-white'
                                        }`}>
                                        {course.courseType === 'hybrid' ? 'Hybrid' : 'Online'}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-5">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">{course.courseTitle || 'Course'}</h3>
                                <p className="text-xs text-gray-500 dark:text-text-muted mb-4">
                                    Your split:{' '}
                                    <span className="text-red-400 font-bold">
                                        {course.fixedAmount > 0
                                            ? `Rp ${course.fixedAmount.toLocaleString('id-ID')} fixed`
                                            : `${course.splitPercentage}%`}
                                    </span>
                                </p>

                                <div className="flex items-center gap-3 mb-4">
                                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-text-muted">
                                        <span className="material-symbols-outlined text-[14px]">folder</span>
                                        <span>{course.moduleCount || 0} modules</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-text-muted">
                                        <span className="material-symbols-outlined text-[14px]">group</span>
                                        <span>{course.enrollmentCount || 0} peserta</span>
                                    </div>
                                </div>

                                <Link
                                    to={`/instructor/courses/${course.courseId}/modules`}
                                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[18px]">edit</span>
                                    Manage Modules
                                </Link>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}

export default InstructorCoursesPage
